import React, { useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import cn from "classnames";
import { FormattedMessage, FormattedHTMLMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";

import { battleOfTheHunsId, christmasId } from "../../../../common/parameters";
import { PublishButton } from "../../../../common/styles";

import routing from "../../../routing";
import { IUser } from "../../../store/user/models";
import refreshPlan from "../../../store/plan/actions/refreshPlan";
import getPlanMeta from "../../../store/plan/actions/getPlanMeta";
import { post } from "../../../actions/general";

import Tooltip from "../../../components/Tooltip";
import CloneModal from "../../../components/CloneModal";

import QuickMeta from "../QuickMeta";
import NewHomeworkModal from "../NewHomeworkModal";
import { IStateTree } from "../../../store/interfaces";
import Icon from "../../../icon";
import { Alert } from "../UpperRow/styles";

import messages from "./messages";
import { StyledMenu, MenuLeft, MenuRight, MenuElement, AlertIconWrapper, Center, TotalViews, CovidHint } from "./styles";

import PublishModal from "./PublishModal";
import { IProps } from "./interfaces";


const Menu = ({
    plan,
    type,
    publicView,
    simulation,
    pulsatingMeta,
    pulsatingRefresh,
    pulsatingPublish,
    notLoggedIn,
    isCovid,
}: IProps) => {
    const dispatch = useDispatch();
    const { formatMessage } = useIntl();
    const history = useHistory();
    const [newHomeworkModal, setNewHomeworkModal] = useState(false);
    const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
    const [showCloneModal, setShowCloneModal] = useState(false);
    const [metaModal, setMetaModal] = useState(false);
    const user = useSelector((state: IStateTree) => state.user) as IUser;
    useEffect(() => {
        if (history.location.search === "?publishing") {
            setIsPublishModalOpen(true);
        }
    }, []);
    const openPublishModel = () => {
        setIsPublishModalOpen(true);
    };
    const closePublishModal = () => {
        setIsPublishModalOpen(false);
    }
    const toggleNewHomework = () => {
        setNewHomeworkModal(old => !old);
    };
    const clonePublic = () => {
        setShowCloneModal(true);
    };
    const finishClonePublic = ({ name, selectedPack }: { name: string, selectedPack: string }) => {
        dispatch(post("clone-plan", { _id: plan._id, name, selectedPack }, (res: AxiosResponse<{ limit: boolean, _id: string}>) => {
            const { data } = res;
            if (data.limit) {
                alert("Limit reached! Contact info@musicators.com to create more.");
                setShowCloneModal(false);
            } else if (data._id) {
                const { _id } = data;
                history.push("/" + routing.plan + "/" + _id);
            }
        }));
    };
    const closeCloneModal = () => {
        setShowCloneModal(false);
    };
    const isExtra = plan._id === battleOfTheHunsId || plan._id === christmasId;
    const isExperienceNotSet = plan.experience?.enabled && plan.experience.positions.filter(i => i !== null).length === 0;

    return (
        <StyledMenu isCovid={isCovid}>
            {!isCovid ? (
                <MenuLeft simulation={simulation}>
                    <MenuElement
                        replace
                        exact
                        to={simulation ? "/" : `/${type}/${plan._id}`}
                        activeClassName={"Active"}
                    >
                        <FormattedMessage {...messages.tasks} />
                    </MenuElement>
                    {!publicView && (
                        <MenuElement
                            replace
                            exact
                            to={simulation ? "/" : `/${type}/${plan._id}/${routing.scores}`}
                            activeClassName={"Active"}
                        >
                            <FormattedMessage {...messages.score} />
                        </MenuElement>
                    )}
                    {(!publicView || plan.experience.enabled) && (
                        <MenuElement
                            replace
                            rel="nofollow"
                            to={`/${type}/${plan._id}/${routing.experience}`}
                            activeClassName={"Active"}
                        >
                            <FormattedMessage {...messages.experience} />
                            {isExperienceNotSet && (
                                <Tooltip label={<FormattedHTMLMessage {...messages.invalidExperince} />} marginTop={12}>
                                    <AlertIconWrapper>
                                        <Icon i="alert" />
                                    </AlertIconWrapper>
                                </Tooltip>
                            )}
                        </MenuElement>
                    )}
                    <MenuElement
                        replace
                        rel="nofollow"
                        to={`/${type}/${plan._id}/${routing.meta}`}
                        activeClassName={"Active"}
                        onClick={() => {
                            if (!publicView) {
                                dispatch(getPlanMeta());
                            }
                        }}
                    >
                        <FormattedMessage {...messages.meta} />
                    </MenuElement>
                </MenuLeft>
            ) : (
                <MenuLeft>
                    <CovidHint>
                        <FormattedMessage {...messages.covidUse} /> <Icon i="arrowback" />
                    </CovidHint>
                </MenuLeft>
            )}
            <MenuRight isCovid={isCovid}>
                {publicView && !isCovid && (
                    <TotalViews>
                        <Icon i="view" />
                        {plan.views}
                    </TotalViews>
                )}
                {!isExtra && publicView && !plan.copyright && (
                    <Tooltip
                        label={
                            notLoggedIn ? (
                                <FormattedMessage {...messages.logInToGadgets} />
                            ) : (
                                <FormattedMessage {...messages.clonePublic} />
                            )
                        }
                        grey={notLoggedIn}
                    >
                        <PublishButton className={cn(user.loggedIn && "Active")} disabled={notLoggedIn}>
                            <a
                                style={{ cursor: notLoggedIn ? "default" : "pointer" }}
                                className={cn(notLoggedIn && "QuickButtonDisabled", "Duplicate")}
                                onClick={() => {
                                    if (!notLoggedIn && !simulation) {
                                        clonePublic();
                                    }
                                }}
                            >
                                <Icon i="duplicate" />
                            </a>
                        </PublishButton>
                    </Tooltip>
                )}
                {!plan.shared && !publicView && (
                    <Tooltip label={
                        <Center>
                            {isExperienceNotSet ? (
                                <FormattedHTMLMessage {...messages.invalidExperince} />
                            ) : !plan.sharable ? (
                                <FormattedHTMLMessage {...messages.labelNotPlayable} />
                            ) : (
                                <FormattedHTMLMessage {...messages.label} />
                            )}
                        </Center>
                    } grey={!plan.sharable}>
                        <PublishButton
                            disabled={!plan.sharable}
                            className={cn(plan.sharable && "Active", pulsatingMeta && "pulsate")}
                            onClick={() => {
                                if (!simulation) {
                                    setMetaModal(true);
                                }
                            }}
                        >
                            <Icon i="meta" />
                        </PublishButton>
                    </Tooltip>
                )}
                {plan.shared && !publicView && (
                    <Tooltip
                        label={
                            <Center>
                                {!plan.sharable ? (
                                    <FormattedHTMLMessage {...messages.refreshNotPlayable} />
                                ) : !plan.metaDone ? (
                                    <FormattedHTMLMessage {...messages.refreshNotLabeled} />
                                ) : (
                                    <FormattedMessage {...messages.refresh} />
                                )}
                            </Center>
                        }
                        disabled={plan.modified! < plan.published! || simulation}
                        grey={!plan.sharable || !plan.metaDone}
                    >
                        <PublishButton
                            className={cn(
                                plan.modified! >= plan.published! && plan.sharable && plan.metaDone && "Active",
                                pulsatingRefresh && "pulsate"
                            )}
                            onClick={() => {
                                dispatch(refreshPlan({ planId: plan._id }));
                            }}
                            disabled={plan.modified! < plan.published! || !plan.sharable || !plan.metaDone}
                        >
                            <Icon i="refresh" />
                        </PublishButton>
                    </Tooltip>
                )}
                {plan.sharable && !isExtra && (
                    <Tooltip
                        label={
                            notLoggedIn ? (
                                <FormattedMessage {...messages.logInToGadgets} />
                            ) : plan.modified! > plan.published! ? (
                                <Center>
                                    <FormattedMessage {...messages.newHomework} />
                                    <br />
                                    <FormattedHTMLMessage {...messages.modifiedRefresh} />
                                </Center>
                            ) : (
                                <Center>
                                    <FormattedMessage {...messages.newHomework} />
                                    {!(plan.shared || publicView) && (
                                        <>
                                            <br />
                                            <FormattedMessage {...messages.metaFirst} />
                                        </>
                                    )}
                                </Center>
                            )
                        }
                        grey={!user.loggedIn || !(plan.shared || publicView) || (plan.modified! > plan.published! && !publicView)}
                    >
                        <PublishButton
                            disabled={!user.loggedIn || !(plan.shared || publicView) || (plan.modified! > plan.published! && !publicView)}
                            className={cn(user.loggedIn && (plan.shared || publicView) && (plan.modified! <= plan.published! || publicView) && "Active")}
                            onClick={
                                (user.loggedIn && (plan.shared || publicView) && !simulation)
                                    ? toggleNewHomework
                                    : undefined
                            }
                        >
                            <Icon i="homework" />
                        </PublishButton>
                    </Tooltip>
                )}

                {!publicView && plan.shared && (
                    <Tooltip
                        label={
                            user.verifiedEmail ? (
                                formatMessage(messages.publish)
                            ) : (
                                <div style={{ textAlign: "center" }}>
                                    <FormattedHTMLMessage {...messages.verifyEmail} />
                                </div>
                            )
                        }
                        grey={!user.verifiedEmail}
                    >
                        <PublishButton
                            className={cn(user.verifiedEmail && "Active", pulsatingPublish && "pulsate")}
                            onClick={!simulation ? openPublishModel : undefined}
                            disabled={!user.verifiedEmail}
                        >
                            <Icon i="publish" />
                            {plan.modified! > plan.published! &&
                                plan.public &&
                                !(plan.shared && plan.public && plan.accepted) && <Alert style={{ bottom: 2, right: 2 }}>!</Alert>}
                        </PublishButton>
                    </Tooltip>
                )}
            </MenuRight>
            {metaModal && (
                <QuickMeta
                    close={() => {
                        setMetaModal(false);
                    }}
                    plan={plan}
                />
            )}
            {newHomeworkModal && (
                <NewHomeworkModal
                    close={toggleNewHomework}
                    planId={plan.publicId || plan._id}
                    tasks={plan.tasks}
                    hasExperience={plan.experience.enabled}
                    plan={plan}
                />
            )}
            {isPublishModalOpen && <PublishModal close={closePublishModal} />}
            {showCloneModal && (
                <CloneModal close={closeCloneModal} name={plan.name} submit={finishClonePublic} />
            )}
        </StyledMenu>
    );
}

export default Menu;
